import {
	AppstoreOutlined,
	DollarCircleOutlined,
	FileAddOutlined,
} from "@ant-design/icons"
import { Badge } from "components/Badge/Badge"
import { BaseLayout } from "components/BaseLayout/BaseLayout"
import { Empty } from "components/Empty/Empty"
import { UnauthorizedIcon } from "icons/UnauthorizedIcon"
import { useViewLegal } from "modules/legal/useViewLegal"
import { Notifications } from "modules/notifications/Notifications"
import { useNotificationSettingsMenuOption } from "modules/notifications/useNotificationSettingsMenuOption"
import { ScopeSelect } from "modules/scope/ScopeSelect"
import { useSupportRequests } from "modules/support/useSupportRequests"
import { ComponentProps, useMemo, useRef } from "react"
import { useLocation, useOutlet, useParams } from "react-router"
import { useEnrollmentsCounts } from "services/enrollments"
import { useOrganization, useUserRole } from "services/users"
import { cn } from "styles/cn"

export function DashboardLayout() {
	const { scopeId } = useParams()
	const location = useLocation()
	const organization = useOrganization()
	const {
		data: { fullName, isProvider, isProviderAnon, organizations = [] } = {},
		isFetched,
	} = useUserRole()
	const currentOutlet = useOutlet()
	const previousOutlet = useRef<React.ReactNode>(null)
	const isModalPath = location.pathname.includes("/m/")
	const { data: counts } = useEnrollmentsCounts(
		!!organization?.canEnrollPatients,
	)
	const viewLegal = useViewLegal()
	const supportRequests = useSupportRequests()
	const notificationSettings = useNotificationSettingsMenuOption()
	const items = useMemo<ComponentProps<typeof BaseLayout.Header>["items"]>(
		() => [
			{
				key: `/${scopeId}`,
				icon: <AppstoreOutlined aria-hidden />,
				label: "Dashboard",
			},
			...(organization?.canEnrollPatients
				? [
						{
							key: `/${scopeId}/enrollments`,
							icon: <FileAddOutlined aria-hidden />,
							label: (
								<Badge
									count={counts?.Pending ?? 0}
									offset={[14, -2]}
									classNames={{ indicator: "bg-red-40" }}
								>
									Patient enrollments
								</Badge>
							),
						},
					]
				: []),
			...(isProvider
				? [
						{
							key: `/${scopeId}/billing`,
							icon: <DollarCircleOutlined aria-hidden />,
							label: "Billing",
						},
					]
				: []),
		],
		[counts?.Pending, isProvider, scopeId, organization?.canEnrollPatients],
	)

	// Persist the previous non-modal outlet
	if (!isModalPath) {
		previousOutlet.current = currentOutlet
	}

	return (
		<BaseLayout className={cn(isProviderAnon && "lg:grid-cols-1")}>
			<BaseLayout.Header
				username={fullName}
				items={isProvider ? items : []}
				viewLegalItems={viewLegal.items}
				supportRequestsItems={supportRequests.items}
				supportRequestsUpdatesCount={supportRequests.ownUpdatesCount}
				notificationSettingsItems={notificationSettings.items}
				actions={<Notifications />}
			>
				<ScopeSelect ariaLabel="Select an organization" />
			</BaseLayout.Header>

			{isProvider && (
				<BaseLayout.SideNav
					items={items}
					viewLegalItems={viewLegal.items}
					supportRequestsItems={supportRequests.items}
					supportRequestsUpdatesCount={supportRequests.ownUpdatesCount}
					notificationSettingsItems={notificationSettings.items}
				/>
			)}

			<BaseLayout.Main>
				{organizations.length === 0 && isFetched ? (
					<Empty
						Icon={UnauthorizedIcon}
						title="No organizations found"
						description="Your account is not currently associated with any organizations. Please
									contact Empo Health to be added to an organization."
					/>
				) : (
					<>
						{/* Background Layer: Either the previous page or PatientDetail fallback */}
						{isModalPath ? previousOutlet.current : currentOutlet}

						{/* Foreground Modal Layer: Only when on the screening path */}
						{isModalPath && currentOutlet}
					</>
				)}
			</BaseLayout.Main>

			{viewLegal.viewer}
		</BaseLayout>
	)
}
